import axios from 'axios';
import { ActionTree} from 'vuex';
import {RootState} from '@/store/types';
import {TemplateItem, TemplatesState, TemplatesFilterParams} from './types';
import {directDownload, transformTransport} from "@/utils/download-helper";

export const actions: ActionTree<TemplatesState, RootState> = {
  async getTemplates({commit}, data: {filter: TemplatesFilterParams, transport: string}) {
    try {
      const resp = await axios({
        url: `/lk-api/distribution/${transformTransport(data.transport)}/template`,
        params: data.filter,
      })
      const items = resp.data.map((item:any) => {
        if (item?.content?.name) item.message = item.content.name
        if (typeof item?.content === 'string') item.message = item.content
        item.showDraft = false
        return item
      })
      commit(`setTemplates`, items);
      return items;
    } catch (err) {
      throw err;
    }
  },
  async exportTemplates({commit, dispatch}, data: {filter: TemplatesFilterParams, transport: string}) {
    return await directDownload(`/${transformTransport(data.transport)}/templates/export`, data.filter)
  },
  async getMultichannelTemplates({commit}, data: {filter: TemplatesFilterParams, transport: string}) {
    commit(`setMultichannelTemplates`, []);
    try {
      const resp = await axios({
        url: `/lk-api/distribution/${transformTransport(data.transport)}/template`,
        params: data.filter,
      })
      commit(`setMultichannelTemplates`, resp.data);
      return resp;
    } catch (err) {
      throw err;
    }
  },
  async getMultichannelTemplate({commit}, data) {
    try {
      const resp = await axios({url: `/lk-api/distribution/multichannel`, params: data})
      return resp.data?.templates;
    } catch (err) {
      throw err;
    }
  },
  async getTemplateItem({commit, dispatch}, data: {id: number, transport: string}){
    try {
      const resp = await axios({
        url: `/lk-api/distribution/${transformTransport(data.transport)}/template?templateId=${data.id}`,
        params: {cache: Math.random()},
      })
      let template = resp.data;
      template.senderId = resp.data?.sender?.id
      template.messageTypeId = resp.data?.messageType?.id
      if (resp.data?.blacklists) {
        template.blacklistIds = resp.data?.blacklists.map((item: any) => item.id)
      }
      template.multichannelId = resp.data?.multichannel?.id
      template.multichannelTemplateId = resp.data?.multichannelTemplate?.id
      delete template.sender
      delete template.messageType
      delete template.multichannel
      delete template.multichannelTemplate

      if (data.transport == 'Push') {
        template.applicationId = resp.data?.application?.id
        delete template.application
        if (template.customData) {
          template.customData = JSON.stringify(template.customData)
        }
      }
      if (data.transport == 'Call') {
        template.smsTemplateId = resp.data?.smsTemplate?.id
        delete template.smsTemplate
        if (resp.data?.content?.id) template.message = resp.data?.content?.id
        if (typeof resp.data?.content === 'string') template.message = resp.data?.content
        delete template.content
      }
      if (data.transport == 'Mail Notify') {
        template.mnServiceId = resp.data?.mnService?.id
        delete template.mnService
        if (resp.data?.mnTemplate?.id) {
          template.mnTemplateId = resp.data?.mnTemplate?.id
          delete template.mnTemplate
        }
      }
      commit(`setTemplateItem`, template);
      return template;
    } catch (err) {
      throw err;
    }
  },
  async saveTemplate({commit, dispatch}, data: {template: TemplateItem, transport: string}) {
    try {
      const resp = await axios({
        method: data.template.templateId ? 'put' : 'post',
        url: `/lk-api/distribution/${transformTransport(data.transport)}/template`,
        data: data.template,
      })
      return resp;
    } catch (err) {
      throw err;
    }
  },
  async deleteTemplate({commit, dispatch}, data: {template: TemplateItem, transport: string}) {
    try {
      const resp = await axios({
        method: 'delete',
        url: `/lk-api/distribution/${transformTransport(data.transport)}/template`,
        data: data.template,
      })
      return resp;
    } catch (err) {
      throw err;
    }
  },

  async checkTemplate({commit, dispatch}, data) {
    try {
      const resp = await axios({
        url: '/lk-api/distribution/template',
        params: data,
      })
      return resp;
    } catch (err) {
      throw err;
    }
  },

  async createDraft({commit, dispatch}, data) {
    try {
      const resp = await axios({
        method: 'post',
        url: '/lk-api/distribution/template/draft',
        data: data,
      })
      return resp;
    } catch (err) {
      throw err;
    }
  },

  async sendToModeration({commit, dispatch}, data) {
    try {
      const resp = await axios({
        method: 'put',
        url: '/lk-api/distribution/template/moderate',
        data: data,
      })
      return resp;
    } catch (err) {
      throw err;
    }
  },

  async replaceDraft({commit, dispatch}, data) {
    try {
      const resp = await axios({
        method: 'put',
        url: '/lk-api/distribution/template/replace',
        data: data,
      })
      return resp;
    } catch (err) {
      throw err;
    }
  },

  async getDistributions({commit, dispatch}, data) {
    try {
      let resp = await axios({
        url: 'lk-api/distribution',
        params: data,
      });
      let items = resp.data.map((item: any) => {
        item.status = item.status.name
        return item
      })
      if (data?.page_number > 1) {
        commit('pushDistributions', items);
      } else {
        commit('setDistributions', items);
      }
      return resp;
    } catch (err) {
      throw err;
    }
  },

  async createDistribution({commit, dispatch}, data: {distribution: TemplateItem, transport: string}) {
    try {
      const resp = await axios({
        method: 'post',
        url: `/lk-api/distribution/${transformTransport(data.transport)}`,
        data: data.distribution,
      })
      return resp;
    } catch (err) {
      throw err;
    }
  },

  async deleteDistribution({commit, dispatch}, data) {
    try {
      let resp = await axios({
        url: 'lk-api/distribution',
        method: 'delete',
        data: data,
      });
      return resp;
    } catch (err) {
      throw err;
    }
  },

  async startDistribution({commit, dispatch}, batchId) {
    return await axios({url: 'lk-api/distribution/start?batchId='+batchId});
  },

  async stopDistribution({commit, dispatch}, batchId) {
    return await axios({url: 'lk-api/distribution/stop?batchId='+batchId});
  },

  async uploadFiles({commit, dispatch}, data) {
    try {
      const resp = await axios({
        method: 'post',
        url: `/lk-api/files`,
        data,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return resp;
    } catch (err) {
      throw err;
    }
  },

  async getFile({commit, dispatch}, id) {
    try {
      const resp = await axios({
        method: 'get',
        url: `/lk-api/files/${id}`,
      })
      return resp;
    } catch (err) {
      throw err;
    }
  },

  async deleteFile({commit, dispatch}, id) {
    try {
      const resp = await axios({
        method: 'delete',
        url: `/lk-api/files/${id}`,
      })
      return resp;
    } catch (err) {
      throw err;
    }
  },

};